import React from "react";

/**
 * Thermal receipt handed to a runner at the finish desk: they give their bib,
 * the operator prints their time.
 *
 * 80mm roll. The paper is 80mm wide but the printable area is ~72mm, so the
 * sheet is sized to the paper and the content padded in — printing at the full
 * 80mm clips the right edge on the Kassen head. Height is `auto` because the
 * roll is continuous: fixing it to a page size feeds blank paper.
 *
 * Only this element survives `window.print()` — see `receipt-print.css`, which
 * hides the app and lifts the receipt to the top-left of the sheet. The visible
 * on-screen copy is a preview at the same width, so what the operator checks is
 * what the roll produces.
 */

const PRINT_STYLE_ID = "vx-receipt-print-style";

/** Injected once. Kept out of the component tree so a re-render cannot drop it
 *  mid-print, which produced a receipt with the whole page printed behind it. */
const ensurePrintStyles = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(PRINT_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = PRINT_STYLE_ID;
  style.textContent = `
@media print {
  @page { size: 80mm auto; margin: 0; }
  html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
  body * { visibility: hidden !important; }
  #vx-receipt, #vx-receipt * { visibility: visible !important; }
  #vx-receipt {
    position: absolute !important;
    left: 0 !important; top: 0 !important;
    width: 80mm !important;
    margin: 0 !important;
    box-shadow: none !important;
    border: 0 !important;
  }
}`;
  document.head.appendChild(style);
};

/** Fires the OS print dialog. The browser cannot reach a printer any other way:
 *  there is no silent-print API, so the operator picks the Kassen once and the
 *  dialog remembers it. */
export const printFinishReceipt = () => {
  ensurePrintStyles();
  // Let React paint the receipt before the dialog freezes the page; without the
  // frame the first print of a session came out blank.
  requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
};

const Row = ({ children, className = "" }) => (
  <div className={`text-center ${className}`}>{children}</div>
);

const Rule = () => (
  <div
    aria-hidden="true"
    style={{ borderTop: "1px dashed #000", margin: "2.5mm 0" }}
  />
);

/**
 * @param {string} eventName   Event title, printed as the header.
 * @param {string} bibName     The name ON the bib — what the runner recognises.
 * @param {string} bib         Bib number.
 * @param {string} categoryName
 * @param {string} finishTime  Formatted HH:MM:SS, already chosen by the caller.
 */
export const FinishReceipt = ({
  eventName,
  bibName,
  bib,
  categoryName,
  finishTime,
  gender,
  genderRank,
  overallRank,
}) => {
  ensurePrintStyles();

  const genderLabel =
    gender === "female" ? "FEMALE" : gender === "male" ? "MALE" : null;
  const hasRank = overallRank > 0 || genderRank > 0;

  return (
    <div
      id="vx-receipt"
      style={{
        width: "80mm",
        // 4mm each side keeps the text inside the ~72mm printable band.
        padding: "4mm",
        boxSizing: "border-box",
        background: "#fff",
        color: "#000",
        // Monospace so digits line up column-wise on the roll; the generic
        // fallback matters because thermal drivers rarely carry a UI font.
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: "11pt",
        lineHeight: 1.35,
      }}
    >
      <Row>
        <div style={{ fontSize: "10pt", fontWeight: 700, letterSpacing: "0.5px" }}>
          {eventName}
        </div>
      </Row>

      <Rule />

      <Row>
        <div style={{ fontSize: "13pt", fontWeight: 700, letterSpacing: "1px" }}>
          BIB {bib}
        </div>
        <div
          style={{
            fontSize: "15pt",
            fontWeight: 700,
            margin: "1.5mm 0",
            // A long bib name must wrap rather than run off the roll.
            wordBreak: "break-word",
          }}
        >
          {bibName}
        </div>
        <div style={{ fontSize: "9pt" }}>{categoryName}</div>
      </Row>

      <Rule />

      <Row>
        <div style={{ fontSize: "9pt", letterSpacing: "1px" }}>FINISH TIME</div>
        <div
          style={{
            fontSize: "22pt",
            fontWeight: 700,
            letterSpacing: "1px",
            margin: "1mm 0",
          }}
        >
          {finishTime}
        </div>
      </Row>

      {/* Placing within the runner's own category. Omitted entirely when it is
          not known yet, rather than printing a bare dash the runner would read
          as a result. */}
      {hasRank ? (
        <>
          <Rule />
          <Row>
            <div style={{ display: "flex", justifyContent: "center", gap: "8mm" }}>
              {overallRank > 0 ? (
                <div>
                  <div style={{ fontSize: "8pt" }}>OVERALL</div>
                  <div style={{ fontSize: "15pt", fontWeight: 700 }}>
                    {overallRank}
                  </div>
                </div>
              ) : null}
              {genderRank > 0 ? (
                <div>
                  <div style={{ fontSize: "8pt" }}>
                    {genderLabel || "GENDER"}
                  </div>
                  <div style={{ fontSize: "15pt", fontWeight: 700 }}>
                    {genderRank}
                  </div>
                </div>
              ) : null}
            </div>
          </Row>
        </>
      ) : null}

      <Rule />

      <Row>
        <div style={{ fontSize: "10pt", fontWeight: 700, letterSpacing: "1px" }}>
          UNOFFICIAL RESULT
        </div>
        <div style={{ fontSize: "7.5pt", marginTop: "1mm" }}>
          Official results follow the protest period.
        </div>
      </Row>

      {/* Feed room: models without an auto-cutter tear here, and without the
          trailing space the tear runs through the last line of text. */}
      <div style={{ height: "10mm" }} aria-hidden="true" />
    </div>
  );
};

export default FinishReceipt;
